import { useMemo, useState } from "react";
import { Box, TextField } from "@mui/material";
import {
  Button,
  DrawerContent,
  DrawerNavBar,
  Icon,
  Typography,
  type DialogComponentProps,
} from "@platform/design-system";
import { extractErrorMessage } from "@platform/api";
import { logger } from "@platform/browser-logger";

import { JsonTree } from "./JsonTree";
import { collectBranchPaths } from "./jsonTreePaths";
import { useInspectClient, useInspectRfp } from "./inspect";

import type { InspectorTarget } from "./types";

const isInspectorTarget = (
  data: Record<string, unknown> | undefined,
): data is InspectorTarget =>
  data !== undefined &&
  (data["type"] === "rfp" || data["type"] === "client") &&
  typeof data["id"] === "number";

const titleForTarget = (target: InspectorTarget): string =>
  target.type === "rfp" ? `RFP #${target.id}` : `Client #${target.id}`;

/**
 * Registered in the app's DialogService dialogRegistry under "recordInspector".
 * Open it via `useDialogService().openDialog({ type: "recordInspector", data: target, width: 800 })`.
 */
export const RecordInspectorDrawer = ({
  data,
  onClose,
}: DialogComponentProps): React.JSX.Element | null => {
  const [filter, setFilter] = useState("");
  const [collapsedPaths, setCollapsedPaths] = useState<Set<string>>(new Set());

  const target = isInspectorTarget(data) ? data : undefined;

  const rfpQuery = useInspectRfp(
    target?.type === "rfp" ? target.id : 0,
    target?.type === "rfp",
  );
  const clientQuery = useInspectClient(
    target?.type === "client" ? target.id : 0,
    target?.type === "client",
  );

  const activeQuery = target?.type === "rfp" ? rfpQuery : clientQuery;
  const recordData = activeQuery.data;

  const branchPaths = useMemo(
    () =>
      recordData ? collectBranchPaths(recordData, "").filter(Boolean) : [],
    [recordData],
  );

  const toggle = (path: string): void => {
    setCollapsedPaths((prev) => {
      const next = new Set(prev);

      if (next.has(path)) {
        next.delete(path);
      } else {
        next.add(path);
      }

      return next;
    });
  };

  const copyAll = (): void => {
    if (!recordData || !navigator.clipboard?.writeText) {
      return;
    }

    navigator.clipboard
      .writeText(JSON.stringify(recordData, null, 2))
      .catch((error: unknown) => {
        logger.error(
          "[EntityExplorer] Failed to copy record as JSON",
          error instanceof Error ? error : undefined,
        );
      });
  };

  if (!target) {
    return null;
  }

  return (
    <>
      <DrawerNavBar>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
          }}
        >
          <Box>
            <Typography variant="header-small">
              {titleForTarget(target)}
            </Typography>
            <Typography
              variant="caption"
              color="textSecondary"
              sx={{ fontStyle: "italic" }}
            >
              Viewing this record is logged for audit
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button variant="tertiary" size="sm" onClick={copyAll}>
              <Icon name="copy" size="sm" /> Copy as JSON
            </Button>
            <Button
              variant="tertiary"
              size="sm"
              aria-label="Close"
              onClick={() => onClose()}
            >
              <Icon name="close" size="sm" />
            </Button>
          </Box>
        </Box>
      </DrawerNavBar>

      <Box
        sx={{
          display: "flex",
          gap: 1,
          p: 2,
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <TextField
          size="small"
          sx={{ flex: 1, minWidth: 0 }}
          placeholder="Filter fields…"
          value={filter}
          onChange={(event) => setFilter(event.target.value)}
          slotProps={{
            input: {
              startAdornment: <Icon name="search" size="sm" />,
            },
          }}
        />
        <Button
          variant="tertiary"
          size="sm"
          sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
          onClick={() => setCollapsedPaths(new Set())}
        >
          Expand all
        </Button>
        <Button
          variant="tertiary"
          size="sm"
          sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
          onClick={() => setCollapsedPaths(new Set(branchPaths))}
        >
          Collapse all
        </Button>
      </Box>

      <DrawerContent>
        {activeQuery.isLoading && (
          <Typography variant="body-medium">Loading…</Typography>
        )}
        {activeQuery.isError && (
          <Typography variant="body-medium" color="textSecondary">
            {extractErrorMessage(activeQuery.error, "Failed to load record")}
          </Typography>
        )}
        {recordData && (
          <JsonTree
            data={recordData}
            collapsedPaths={collapsedPaths}
            onToggle={toggle}
            filter={filter.trim()}
          />
        )}
      </DrawerContent>
    </>
  );
};
